import { Button, TextField, Tooltip } from '@atgfr8/atg-ui-tailwind';
import { currencyTypes } from '@common/commonLists';
import { BasicModal, SelectInput } from '@components';
import { SelectInputCustom } from '@components/Select';
import { FeatureFlags } from '@constants/featureFlags';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { closeModal, openModal } from '@store/modals';
import {
  carrierStore,
  orderStore,
  updateLoadPropertyAtIndex,
  validateFieldForOrder,
} from '@store/orders';
import {
  getPendingRateRequest,
  isMaxRateRequestEnabled,
} from '@store/orders/actions/rateRequestActions';
import { getOrderCarrierMaxRateBlock, isRep } from '@store/orders/selectors';
import { ILoadEditViewModel } from '@typeDefinitions/loadTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { isAdmin, isSuperAdmin } from '@utils/utils';
import { createSignal, Show } from 'solid-js';

import { HoldPayment } from './HoldPayment';
import { MaxRateRequestActions } from './maxRateRequest/MaxRateRequestActions';
import { MaxRateRequestBanner } from './maxRateRequest/MaxRateRequestBanner';

type Props = {
  load: ILoadEditViewModel;
  tabIndex: number;
};

const CarrierPayAdditionalInfo = (props: Props) => {
  const billingHoldingModalId = `billingHoldReason-${props.tabIndex}`;
  const [paymentStatus, setPaymentStatus] = createSignal<string>('');

  const canLoadRepEditMaxRate = () =>
    isFeatureFlagEnabled(FeatureFlags.PermissionAllowLoadRepMaxPayUpdate)() &&
    isRep();

  const isMaxRateReadOnly = () =>
    ['TL', 'Partial'].includes(props.load.mode ?? '') &&
    isFeatureFlagEnabled(FeatureFlags.CarrierPayAllowMaxPayUpdate)() &&
    !canLoadRepEditMaxRate();

  // Hidden on unsaved loads. A pending request stays visible after reload.
  const showRequestMaxRateIncrease = () =>
    props.load.id > 0 &&
    ((isMaxRateReadOnly() &&
      getOrderCarrierMaxRateBlock(props.load) === 'booked') ||
      (isMaxRateRequestEnabled() &&
        getPendingRateRequest(props.load.id) !== null));

  // Show only for TL/Partial, not LTL or read-only.
  const showMaxRatePermissionTooltip = () =>
    isMaxRateReadOnly() && !orderStore.isReadOnly;

  const DisabledMaxRateField = () => (
    <TextField
      disabled={true}
      size="sm"
      class="h-[38px]! w-full text-black!"
      type="number"
      label="Max Rate"
      maxLength={10}
      placeholder="Add Rate"
      value={props.load.maxPrice ?? ''}
      dataTestId={'max-rate-textInput'}
      error={
        orderStore.orderFormError &&
        orderStore.orderFormError[`loads[${props.tabIndex}].maxPrice`]
      }
    />
  );

  const onPaymentStatusChange = (value: string) => {
    setPaymentStatus(value);
    switch (value) {
      case 'Hold Carrier Pay':
        openModal(billingHoldingModalId);
        break;
      case 'Release Carrier Pay':
        updateLoadPropertyAtIndex({
          billingHold: false,
          billingHoldReason: '',
          billingHoldDescription: '',
        });
        setPaymentStatus('');
        break;
    }
  };
  return (
    <>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
        <Show when={isSuperAdmin() || isAdmin()}>
          <div class="ml-auto max-w-[170px] w-full flex ">
            <SelectInputCustom
              size="sm"
              dataTestId="carrier-pay-status-select"
              menuItems={[
                { label: 'Hold Carrier Pay', value: 'Hold Carrier Pay' },
                {
                  label: 'Release Carrier Pay',
                  value: 'Release Carrier Pay',
                },
              ]}
              renderValue={() => {
                if (orderStore.order.loads[props.tabIndex].billingHold) {
                  return <div class="mt-1">Release Carrier Pay</div>;
                }
                return <div>Hold Carrier Pay</div>;
              }}
              value={paymentStatus()}
              onChange={onPaymentStatusChange}
              disabled={orderStore.isReadOnly}
              style={{
                'background-color': orderStore.order.loads[props.tabIndex]
                  .billingHold
                  ? '#5da05b'
                  : '#B00020',
                color: 'white',
              }}
              class="text-white! border-none! [&_span]:text-white!"
              iconColor="white"
            />
          </div>
        </Show>
        <div
          class={`md:col-span-2 grid grid-cols-1 items-center gap-2 ${
            showRequestMaxRateIncrease()
              ? 'md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]'
              : 'md:grid-cols-2'
          }`}
        >
          <div class="w-full min-w-0">
            <TextField
              // required={props.load.mode !== 'LTL'}
              disabled={props.load.mode === 'LTL' || orderStore.isReadOnly}
              size="sm"
              class="h-[38px]!"
              type="number"
              label="Starting Rate"
              placeholder="Add Rate"
              maxLength={10}
              value={props.load.minPrice ?? ''}
              onChange={(value: number | string) => {
                const value1 = value === '' ? null : value;
                updateLoadPropertyAtIndex({
                  minPrice: value1 as number,
                });
              }}
              on:blur={() =>
                validateFieldForOrder(
                  `loads.${props.tabIndex}.minPrice`,
                  `loads[${props.tabIndex}].minPrice`,
                )
              }
              style={{ width: '100%', height: '38px' }}
              error={
                orderStore.orderFormError &&
                orderStore.orderFormError[`loads[${props.tabIndex}].minPrice`]
              }
              dataTestId={'starting-rate-textInput'}
            />
          </div>
          <div class="w-full min-w-0">
            <Show
              when={
                !(
                  props.load.mode === 'LTL' ||
                  orderStore.isReadOnly ||
                  isMaxRateReadOnly()
                )
              }
              fallback={
                <Show
                  when={showMaxRatePermissionTooltip()}
                  fallback={<DisabledMaxRateField />}
                >
                  <Tooltip
                    title="Only load reps or approved users can edit Max Rate."
                    placement="bottom-start"
                    class="z-10 w-full"
                    dataTestId="max-rate-notice-tooltip"
                  >
                    <DisabledMaxRateField />
                  </Tooltip>
                </Show>
              }
            >
              <TextField
                size="sm"
                class="h-[38px]!"
                type="number"
                label="Max Rate"
                maxLength={10}
                style={{
                  width: '100%',
                  height: '38px',
                }}
                placeholder="Add Rate"
                value={props.load.maxPrice ?? ''}
                onChange={(value: number | string) => {
                  const value1 = value === '' ? null : value;
                  updateLoadPropertyAtIndex({
                    maxPrice: value1 as number,
                  });
                }}
                on:blur={() => {
                  void validateFieldForOrder(
                    `loads.${props.tabIndex}.maxPrice`,
                    `loads[${props.tabIndex}].maxPrice`,
                  );
                  void validateFieldForOrder(
                    `loads.${props.tabIndex}.maxRateError`,
                    `loads[${props.tabIndex}].maxRateError`,
                    { load: props.load },
                  );
                }}
                error={
                  orderStore.orderFormError &&
                  orderStore.orderFormError[`loads[${props.tabIndex}].maxPrice`]
                }
                dataTestId={'max-rate-textInput'}
              />
            </Show>
          </div>
          <Show when={showRequestMaxRateIncrease()}>
            <Show
              when={isMaxRateRequestEnabled()}
              fallback={
                <Button
                  class="rounded-[4px] whitespace-nowrap shrink-0"
                  variant="outlined"
                  size="sm"
                  dataTestId="request-max-rate-increase-button"
                >
                  Request Max Rate Increase
                </Button>
              }
            >
              <MaxRateRequestActions load={props.load} />
            </Show>
          </Show>
        </div>
        <Show
          when={
            (carrierStore.carrier.length > 0 &&
              carrierStore.carrier[orderStore.activeTab.index]
                ?.currencyOptionEnabled) ||
            (carrierStore.carrier.length > 0 &&
              carrierStore.featureFlags[orderStore.activeTab.index]
                ?.triumphPayCurrency)
          }
        >
          <div class="w-full md:col-span-1">
            <SelectInput
              name="currencyType"
              label="Currency Type"
              menuItems={currencyTypes.map((c) => ({
                label: c,
                value: c,
              }))}
              value={props.load.currencyType}
              onChange={(value) => {
                updateLoadPropertyAtIndex({
                  currencyType: value,
                });
              }}
              disabled={orderStore.isReadOnly}
              dataTestId={formatDataTestId('Currency Type', 'selectInput')}
            />
          </div>
        </Show>
      </div>
      <MaxRateRequestBanner loadId={props.load.id} />
      <BasicModal
        id={billingHoldingModalId}
        title="Billing Hold Reason"
        footer={false}
        showClose={false}
        onClose={() => {
          setPaymentStatus('');
          closeModal(billingHoldingModalId);
        }}
        width="70vw"
        modalStyles={{
          background: 'white',
          borderRadius: '5px',
        }}
      >
        <HoldPayment
          modalId={billingHoldingModalId}
          setPaymentStatus={setPaymentStatus}
          tabIndex={props.tabIndex}
          initialValues={{
            billingHoldReason: props.load.billingHoldReason ?? '',
            billingHoldDescription: props.load.billingHoldDescription ?? '',
          }}
        />
      </BasicModal>
    </>
  );
};

export default CarrierPayAdditionalInfo;
